import client from "./client";
import { deviceIdHeaders } from "../lib/deviceId";
import type {
  StreetChatStreamInfo,
  StreamSubscribeInfo,
  StreamPublishInfo,
} from "../types/stream";

export async function getGcStreamSlots(
  stcId: number,
  gcId: number,
  signal?: AbortSignal,
): Promise<StreetChatStreamInfo> {
  const res = await client.get<{ result: StreetChatStreamInfo }>(
    `/api/stream/get-gc-slot/stc/${stcId}/gc/${gcId}`,
    { signal },
  );
  return res.data.result;
}

export async function subscribeToSlot(
  slotId: number,
): Promise<StreamSubscribeInfo> {
  const res = await client.post<{ result: StreamSubscribeInfo }>(
    `/api/stream/subscribe-to-stream/${slotId}`,
  );
  return res.data.result;
}

/** main-api answers `result: true` (no slot); positions come from the slot list and events. */
export async function subscribeToMediaStream(slotId: number): Promise<boolean> {
  const res = await client.post<{ result: boolean }>(
    `/api/stream/subscribe-to-media-stream/${slotId}`,
  );
  return res.data.result;
}

/** room-stc's clock for cast sync (spec 2026-10-12 cast sync §1): server epoch ms. */
export async function getCastServerTime(): Promise<number> {
  const res = await client.get<{ result: { serverTime: number } }>(
    "/api/content/cast/time",
    { timeout: 3000 },
  );
  return res.data.result.serverTime;
}

export async function requestPublish(slotId: number): Promise<StreamPublishInfo> {
  const res = await client.post<{ result: StreamPublishInfo }>(
    `/api/stream/request-publish/${slotId}`,
  );
  return res.data.result;
}

export async function stopStreaming(slotId: number): Promise<void> {
  await client.post(`/api/stream/stop-streaming/${slotId}`);
}

/** For pagehide: survives the page going away. Auth rides on the session cookie. */
export function stopStreamingKeepalive(slotId: number): void {
  fetch(`/api/stream/stop-streaming/${slotId}`, {
    method: "POST",
    keepalive: true,
    credentials: "include",
    headers: deviceIdHeaders(),
  }).catch(() => {});
}

export async function pingSlot(slotId: number): Promise<void> {
  await client.post(`/api/stream/slot/${slotId}/ping-update`);
}

/** Which conversations in the user's street chat have anything live. */
export interface ActiveStreamSummary {
  stcId: number | null;
  liveGcIds: number[];
}

/**
 * Cold-start liveness for the left-menu indicator: one request instead of one
 * per conversation. Requested from the backend; until it ships this 404s and
 * StreamStatusBridge falls back to events alone.
 */
export async function getActiveStreamSummary(): Promise<ActiveStreamSummary> {
  const res = await client.get<{ result: ActiveStreamSummary }>(
    "/api/stream/active-summary",
  );
  return res.data.result;
}

/** Your own media for a file cast (Android StcPickMediaViewModel / MediaResponse; main-api StreamMediaResponse). */
export interface UserMedia {
  id: number;
  mediaType: "VIDEO" | "IMAGE" | string;
  filePath?: string | null;
  presignUrl?: string | null;
  thumbnail?: string | null;
  username?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  width?: number | null;
  height?: number | null;
}

export async function getUserMedia(): Promise<UserMedia[]> {
  const res = await client.post<{ result?: UserMedia[] }>("/api/user-media/get-user-media", {
    types: ["VIDEO", "IMAGE"],
    userId: null,
  });
  return [...(res.data.result ?? [])].sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""));
}
